import { nextTick } from "vue";
import { Router, createRouter, createWebHashHistory, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Actions, Mutations } from "@/store/enums/store.enums";
import utilsService from "@/core/services/utilsService";
import layoutConfig from "@/core/config/layoutConfig";

const titlePrefix = layoutConfig.fullName;
const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/login",
    component: () => import("@/view/layout/index.vue"),
    children: [
      {
        path: "/dashboard",
        name: "vo-dashboard",
        meta: {
          module: "dashboard",
          title: titlePrefix + "Dashboard"
        },
        component: () => import("@/modules/dashboard/index.vue"),
        children: []
      },
      {
        path: "/system",
        name: "vo-system",
        children: [
          {
            path: "",
            name: "vo-system-settings",
            meta: { module: "system", right: "edit", title: titlePrefix + "System Settings" },
            component: () => import("@/modules/system/settings.vue")
          },
          {
            path: "api-logs",
            name: "vo-system-api-logs",
            meta: { module: "system", right: "edit", title: titlePrefix + "API Logs" },
            component: () => import("@/modules/system/api-logs.vue")
          },
          {
            path: "error-logs",
            name: "vo-system-error-logs",
            meta: { module: "system", right: "edit", title: titlePrefix + "Error Logs" },
            component: () => import("@/modules/system/error-logs.vue")
          },
          {
            path: "oauth/amazon",
            name: "vo-system-oauth-amazon",
            props: (route) => ({ ...route.query }),
            meta: { module: "system", right: "edit", title: titlePrefix + "System OAUTH" },
            component: () => import("@/modules/system/oauth.vue")
          }
        ]
      },
      {
        path: "/example",
        name: "vo-example",
        children: [
          {
            path: "",
            name: "vo-example-list",
            meta: { module: "example", right: "list", title: titlePrefix + "Example List" },
            component: () => import("@/modules/example/list.vue")
          },
          {
            path: "new",
            name: "vo-example-new",
            meta: { module: "example", right: "create", title: titlePrefix + "Example New" },
            component: () => import("@/modules/example/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-example-edit",
            meta: { module: "example", right: "edit", title: titlePrefix + "Example New" },
            component: () => import("@/modules/example/edit.vue")
          }
        ]
      },
      {
        path: "/addressbook",
        name: "vo-addressbook",
        children: [
          {
            path: "",
            name: "vo-addressbook-list",
            meta: { module: "addressbook", right: "list", title: titlePrefix + "Addressbook List" },
            component: () => import("@/modules/addressbook/list.vue")
          },
          {
            path: "new",
            name: "vo-addressbook-new",
            meta: { module: "addressbook", right: "create", title: titlePrefix + "Addressbook New" },
            component: () => import("@/modules/addressbook/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-addressbook-edit",
            meta: { module: "addressbook", right: "edit", title: titlePrefix + "Addressbook New" },
            component: () => import("@/modules/addressbook/edit.vue")
          },
          {
            path: "settings",
            name: "vo-addressbook-settings",
            meta: { module: "addressbook", right: "edit", title: titlePrefix + "Addressbook Settings" },
            component: () => import("@/modules/addressbook/settings.vue")
          }
        ]
      },
      {
        path: "/blog",
        name: "vo-blog",
        children: [
          {
            path: "",
            props: route => ({ type: "blog" }),
            name: "vo-blog-list",
            meta: { module: "blog", right: "list", title: titlePrefix + "Blog List" },
            component: () => import("@/modules/cms/list.vue")
          },
          {
            path: "new",
            props: route => ({ type: "blog" }),
            name: "vo-blog-new",
            meta: { module: "blog", right: "create", title: titlePrefix + "Blog New" },
            component: () => import("@/modules/cms/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-blog-edit",
            meta: { module: "blog", right: "edit", title: titlePrefix + "Blog New" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "copy/:id",
            props: route => ({ id: route.params.id, copy: true, type: "blog" }),
            name: "vo-blog-copy",
            meta: { module: "blog", right: "edit", title: titlePrefix + "CMS Copy" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "settings",
            name: "vo-blog-settings",
            meta: { module: "blog", right: "edit", title: titlePrefix + "Blog Settings" },
            component: () => import("@/modules/blog/settings.vue")
          }
        ]
      },
      {
        path: "/newsletter",
        name: "vo-newsletter",
        children: [
          {
            path: "",
            name: "vo-newsletter-list",
            meta: { module: "newsletter", right: "list", title: titlePrefix + "Newsletter" },
            component: () => import("@/modules/newsletter/list.vue")
          },
          {
            path: "new",
            name: "vo-newsletter-new",
            meta: { module: "newsletter", right: "create", title: titlePrefix + "Newsletter Wizard" },
            component: () => import("@/modules/newsletter/wizard.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-newsletter-edit",
            meta: { module: "newsletter", right: "edit", title: titlePrefix + "Newsletter Edit" },
            component: () => import("@/modules/newsletter/wizard.vue")
          },
          {
            path: "stats/:id",
            props: true,
            name: "vo-newsletter-stats",
            meta: { module: "newsletter", right: "list", title: titlePrefix + "Newsletter Stats" },
            component: () => import("@/modules/newsletter/stats.vue")
          },
          {
            path: "settings",
            name: "vo-newsletter-settings",
            meta: { module: "newsletter", right: "edit", title: titlePrefix + "Newsletter Settings" },
            component: () => import("@/modules/newsletter/settings.vue")
          },
          {
            path: "snippets",
            name: "vo-newsletter-snippets",
            meta: { module: "newsletter", right: "list", title: titlePrefix + "Newsletter Snippets" },
            component: () => import("@/modules/newsletter/snippets.vue")
          }
        ]
      },
      {
        path: "/benefits",
        name: "vo-benefits",
        children: [
          {
            path: "",
            props: route => ({ type: "benefit" }),
            name: "vo-benefits-list",
            meta: { module: "benefits", right: "list", title: titlePrefix + "Benefits List" },
            component: () => import("@/modules/cms/list.vue")
          },
          {
            path: "new",
            props: route => ({ type: "benefit" }),
            name: "vo-benefits-new",
            meta: { module: "benefits", right: "create", title: titlePrefix + "Benefit New" },
            component: () => import("@/modules/cms/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-benefits-edit",
            meta: { module: "benefits", right: "edit", title: titlePrefix + "Benefit Edit" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "copy/:id",
            props: route => ({ id: route.params.id, copy: true, type: "benefit" }),
            name: "vo-benefits-copy",
            meta: { module: "benefits", right: "edit", title: titlePrefix + "Benefit Copy" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "settings",
            name: "vo-benefits-settings",
            meta: { module: "benefits", right: "edit", title: titlePrefix + "Benefits Settings" },
            component: () => import("@/modules/benefits/settings.vue")
          }
        ]
      },
      {
        path: "/fahrberichte",
        name: "vo-fahrberichte",
        children: [
          {
            path: "",
            props: route => ({ type: "fahrbericht" }),
            name: "vo-fahrberichte-list",
            meta: { module: "fahrberichte", right: "list", title: titlePrefix + "Fahrberichte List" },
            component: () => import("@/modules/cms/list.vue")
          },
          {
            path: "new",
            props: route => ({ type: "fahrbericht" }),
            name: "vo-fahrberichte-new",
            meta: { module: "fahrberichte", right: "create", title: titlePrefix + "Fahrbericht New" },
            component: () => import("@/modules/cms/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-fahrberichte-edit",
            meta: { module: "fahrberichte", right: "edit", title: titlePrefix + "Fahrbericht Edit" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "copy/:id",
            props: route => ({ id: route.params.id, copy: true, type: "fahrbericht" }),
            name: "vo-fahrberichte-copy",
            meta: { module: "fahrberichte", right: "edit", title: titlePrefix + "Fahrbericht Copy" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "settings",
            name: "vo-fahrberichte-settings",
            meta: { module: "fahrberichte", right: "edit", title: titlePrefix + "Fahrberichte Settings" },
            component: () => import("@/modules/fahrberichte/settings.vue")
          }
        ]
      },
      {
        path: "/services",
        name: "vo-services",
        children: [
          {
            path: "",
            name: "vo-services-list",
            meta: { module: "services", right: "list", title: titlePrefix + "Services" },
            component: () => import("@/modules/services/list.vue")
          },
          {
            path: "new",
            name: "vo-services-new",
            meta: { module: "services", right: "create", title: titlePrefix + "Services New" },
            component: () => import("@/modules/services/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-services-edit",
            meta: { module: "services", right: "edit", title: titlePrefix + "Services Edit" },
            component: () => import("@/modules/services/edit.vue")
          },
          {
            path: "tree/:id",
            props: true,
            name: "vo-services-tree",
            meta: { module: "services", right: "edit", title: titlePrefix + "Services Tree" },
            component: () => import("@/modules/services/tree.vue")
          }
        ]
      },
      {
        path: "/faq",
        name: "vo-faq",
        children: [
          {
            path: "",
            name: "vo-faq-list",
            meta: { module: "faq", right: "list", title: titlePrefix + "FAQ" },
            component: () => import("@/modules/faq/list.vue")
          },
          {
            path: "new",
            name: "vo-faq-new",
            meta: { module: "faq", right: "create", title: titlePrefix + "FAQ New" },
            component: () => import("@/modules/faq/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-faq-edit",
            meta: { module: "faq", right: "edit", title: titlePrefix + "FAQ Edit" },
            component: () => import("@/modules/faq/edit.vue")
          }
        ]
      },
      {
        path: "/shop",
        name: "vo-shop",
        children: [
          {
            path: "",
            name: "vo-shop-list",
            meta: { module: "shop", right: "list", title: titlePrefix + "Shop Settings" },
            component: () => import("@/modules/shop/list.vue")
          }
        ]
      },
      {
        path: "/products",
        name: "vo-products",
        children: [
          {
            path: "",
            name: "vo-products-list",
            meta: { module: "products", right: "list", title: titlePrefix + "Products List" },
            component: () => import("@/modules/products/list.vue")
          },
          {
            path: "new",
            name: "vo-products-new",
            meta: { module: "products", right: "create", title: titlePrefix + "Products New" },
            component: () => import("@/modules/products/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-products-edit",
            meta: { module: "products", right: "edit", title: titlePrefix + "Products Edit" },
            component: () => import("@/modules/products/edit.vue")
          },
          {
            path: "inventory",
            name: "vo-products-inventory",
            meta: { module: "products", right: "list", title: titlePrefix + "Products Inventory" },
            component: () => import("@/modules/products/inventory.vue")
          },
          {
            path: "alerts",
            name: "vo-products-alerts",
            meta: { module: "products", right: "list", title: titlePrefix + "Products Alerts" },
            component: () => import("@/modules/products/alerts.vue")
          },
          {
            path: "receiving",
            name: "vo-products-receiving",
            meta: { module: "products", right: "edit", title: titlePrefix + "Products Receiving" },
            component: () => import("@/modules/products/receiving.vue")
          }
        ]
      },
      {
        path: "/shop-orders",
        name: "vo-shop-orders",
        children: [
          {
            path: "",
            name: "vo-shop-orders-list",
            meta: { module: "shoporders", right: "list", title: titlePrefix + "Shop Orders List" },
            component: () => import("@/modules/shop-orders/list.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-shop-orders-edit",
            meta: { module: "shoporders", right: "edit", title: titlePrefix + "Shop Orders Edit" },
            component: () => import("@/modules/shop-orders/edit.vue")
          },
          {
            path: "export",
            name: "vo-shop-orders-export",
            meta: { module: "shoporders", right: "export", title: titlePrefix + "Shop Orders Export" },
            component: () => import("@/modules/shop-orders/export.vue")
          },
          {
            path: "print/:id",
            props: true,
            name: "vo-shop-orders-print",
            meta: { module: "shoporders", right: "list", title: titlePrefix + "Shop Orders Print" },
            component: () => import("@/modules/shop-orders/print.vue")
          },
          {
            path: "search",
            name: "vo-shop-orders-search",
            meta: { module: "shoporders", right: "list", title: titlePrefix + "Shop Orders Search" },
            component: () => import("@/modules/shop-orders/search.vue")
          },
          {
            path: "shipping",
            name: "vo-shop-orders-shipping",
            meta: { module: "shoporders", right: "list", title: titlePrefix + "Shop Orders Shipping" },
            component: () => import("@/modules/shop-orders/shipping.vue")
          },
          {
            path: "abandoned",
            name: "vo-shop-orders-abandoned",
            meta: { module: "shoporders", right: "list", title: titlePrefix + "Shop Orders Abandoned" },
            component: () => import("@/modules/shop-orders/abandoned.vue")
          }
        ]
      },
      {
        path: "/courses",
        name: "vo-courses",
        children: [
          {
            path: "",
            name: "vo-courses-dashboard",
            meta: { module: "courses", right: "list", title: titlePrefix + "Courses Dashboard" },
            component: () => import("@/modules/courses/dashboard.vue")
          },
          {
            path: "list",
            name: "vo-courses-list",
            meta: { module: "courses", right: "list", title: titlePrefix + "Courses List" },
            component: () => import("@/modules/courses/list.vue")
          },
          {
            path: "new",
            name: "vo-courses-new",
            meta: { module: "courses", right: "create", title: titlePrefix + "Courses New" },
            component: () => import("@/modules/courses/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-courses-edit",
            meta: { module: "courses", right: "edit", title: titlePrefix + "Courses New" },
            component: () => import("@/modules/courses/edit.vue")
          },
          {
            path: "bookings/:id",
            props: true,
            name: "vo-courses-bookings",
            meta: { module: "courses", right: "list", title: titlePrefix + "Courses Bookings" },
            component: () => import("@/modules/courses/bookings.page.vue")
          },
          {
            path: "settings",
            name: "vo-courses-settings",
            meta: { module: "courses", right: "edit", title: titlePrefix + "Courses Settings" },
            component: () => import("@/modules/courses/settings.vue")
          }
        ]
      },
      {
        path: "/subscriptions",
        name: "vo-subscriptions",
        children: [
          {
            path: "",
            name: "vo-subscriptions-dashboard",
            meta: { module: "subscriptions", right: "list", title: titlePrefix + "Subscriptions Dashboard" },
            component: () => import("@/modules/subscriptions/dashboard.vue")
          },
          {
            path: "list",
            name: "vo-subscriptions-list",
            meta: { module: "subscriptions", right: "list", title: titlePrefix + "Subscriptions List" },
            component: () => import("@/modules/subscriptions/list.vue")
          },
          {
            path: "new",
            name: "vo-subscriptions-new",
            meta: { module: "subscriptions", right: "create", title: titlePrefix + "Subscriptions New" },
            component: () => import("@/modules/subscriptions/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-subscriptions-edit",
            meta: { module: "subscriptions", right: "edit", title: titlePrefix + "Subscriptions Edit" },
            component: () => import("@/modules/subscriptions/edit.vue")
          },
          {
            path: "members",
            name: "vo-subscriptions-members",
            meta: { module: "subscriptions", right: "list", title: titlePrefix + "Subscriptions Members" },
            component: () => import("@/modules/subscriptions/members.vue")
          }
        ]
      },
      {
        path: "/accounting",
        name: "vo-accounting",
        children: [
          {
            path: "",
            redirect: { name: "vo-accounting-invoices" }
          },
          {
            path: "invoices",
            name: "vo-accounting-invoices",
            props: { documentType: "invoice" },
            meta: { module: "accounting", right: "list", title: titlePrefix + "Accounting Invoices" },
            component: () => import("@/modules/accounting/list.vue")
          },
          {
            path: "offers",
            name: "vo-accounting-offers",
            props: { documentType: "offer" },
            meta: { module: "accounting", right: "list", title: titlePrefix + "Accounting Offers" },
            component: () => import("@/modules/accounting/list.vue")
          },
          {
            path: "credits",
            name: "vo-accounting-credits",
            props: { documentType: "credit" },
            meta: { module: "accounting", right: "list", title: titlePrefix + "Accounting Credits" },
            component: () => import("@/modules/accounting/list.vue")
          },
          {
            path: ":documentType(invoice|offer|credit)/new",
            name: "vo-accounting-new",
            props: true,
            meta: { module: "accounting", right: "create", title: titlePrefix + "Accounting New" },
            component: () => import("@/modules/accounting/new.vue")
          },
          {
            path: ":documentType(invoice|offer|credit)/edit/:id",
            name: "vo-accounting-edit",
            props: true,
            meta: { module: "accounting", right: "edit", title: titlePrefix + "Accounting Edit" },
            component: () => import("@/modules/accounting/edit.vue")
          },
          {
            path: "products",
            name: "vo-accounting-products",
            meta: { module: "accounting", right: "list", title: titlePrefix + "Accounting Products" },
            component: () => import("@/modules/accounting/products.vue")
          },
          {
            path: "settings",
            name: "vo-accounting-settings",
            meta: { module: "accounting", right: "edit", title: titlePrefix + "Accounting Settings" },
            component: () => import("@/modules/accounting/settings.vue")
          }
        ]
      },
      {
        path: "/tettura",
        name: "vo-tettura",
        children: [
          {
            path: "",
            name: "vo-tettura-listings",
            meta: { module: "tettura", right: "list", title: titlePrefix + "tettura Pläne" },
            component: () => import("@/modules/tettura/listings.vue")
          },
          {
            path: "listing/:id",
            props: true,
            name: "vo-tettura-listing-edit",
            meta: { module: "tettura", right: "edit", title: titlePrefix + "tettura Plan bearbeiten" },
            component: () => import("@/modules/tettura/listing-edit.vue")
          },
          {
            path: "architects",
            name: "vo-tettura-architects",
            meta: { module: "tettura", right: "list", title: titlePrefix + "tettura Architekten" },
            component: () => import("@/modules/tettura/architects.vue")
          },
          {
            path: "architect/:id",
            props: true,
            name: "vo-tettura-architect-edit",
            meta: { module: "tettura", right: "edit", title: titlePrefix + "tettura Architekt bearbeiten" },
            component: () => import("@/modules/tettura/architect-edit.vue")
          },
          {
            path: "reviews",
            name: "vo-tettura-reviews",
            meta: { module: "tettura", right: "list", title: titlePrefix + "tettura Bewertungen" },
            component: () => import("@/modules/tettura/reviews.vue")
          },
          {
            path: "orders",
            name: "vo-tettura-orders",
            meta: { module: "tettura", right: "list", title: titlePrefix + "tettura Bestellungen" },
            component: () => import("@/modules/tettura/orders.vue")
          },
          {
            path: "payouts",
            name: "vo-tettura-payouts",
            meta: { module: "tettura", right: "edit", title: titlePrefix + "tettura Auszahlungen" },
            component: () => import("@/modules/tettura/payouts.vue")
          },
          {
            path: "accounts",
            name: "vo-tettura-accounts",
            meta: { module: "tettura", right: "edit", title: titlePrefix + "tettura Konten" },
            component: () => import("@/modules/tettura/accounts.vue")
          }
        ]
      },
      {
        path: "/reviews",
        name: "vo-reviews",
        children: [
          {
            path: "",
            name: "vo-reviews-list",
            meta: { module: "reviews", right: "list", title: titlePrefix + "Reviews List" },
            component: () => import("@/modules/reviews/list.vue")
          },
          {
            path: "new",
            name: "vo-reviews-new",
            meta: { module: "reviews", right: "create", title: titlePrefix + "Reviews New" },
            component: () => import("@/modules/reviews/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-reviews-edit",
            meta: { module: "reviews", right: "edit", title: titlePrefix + "Reviews Edit" },
            component: () => import("@/modules/reviews/edit.vue")
          }
        ]
      },
      {
        path: "/vouchers",
        name: "vo-vouchers",
        children: [
          {
            path: "",
            name: "vo-vouchers-list",
            meta: { module: "vouchers", right: "list", title: titlePrefix + "Vouchers List" },
            component: () => import("@/modules/vouchers/list.vue")
          },
          {
            path: "new",
            name: "vo-vouchers-new",
            meta: { module: "vouchers", right: "create", title: titlePrefix + "Vouchers New" },
            component: () => import("@/modules/vouchers/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-vouchers-edit",
            meta: { module: "vouchers", right: "edit", title: titlePrefix + "Vouchers Edit" },
            component: () => import("@/modules/vouchers/edit.vue")
          }
        ]
      },
      {
        path: "/deliverylabels",
        name: "vo-deliverylabels",
        children: [
          {
            path: "",
            name: "vo-deliverylabels-list",
            meta: { module: "deliverylabels", right: "list", title: titlePrefix + "Delivery Labels" },
            component: () => import("@/modules/deliverylabels/list.vue")
          },
          {
            path: "new",
            name: "vo-deliverylabels-new",
            meta: { module: "deliverylabels", right: "create", title: titlePrefix + "Delivery Labels New" },
            component: () => import("@/modules/deliverylabels/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-deliverylabels-edit",
            meta: { module: "deliverylabels", right: "edit", title: titlePrefix + "Delivery Labels Edit" },
            component: () => import("@/modules/deliverylabels/edit.vue")
          }
        ]
      },
      {
        path: "/configurator",
        name: "vo-configurator",
        redirect: "/configurator/orders",
        children: [
          {
            path: "orders",
            name: "vo-configurator-orders",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Orders" },
            component: () => import("@/modules/configurator/orders.vue")
          },
          {
            path: "orders/:id",
            name: "vo-configurator-orders-detail",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Order Detail" },
            component: () => import("@/modules/configurator/orders-detail.vue"),
            props: true
          },
          {
            path: "images",
            name: "vo-configurator-list",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator List" },
            component: () => import("@/modules/configurator/list.vue")
          },
          {
            path: "blacklist",
            name: "vo-configurator-blacklist",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Blacklist" },
            component: () => import("@/modules/configurator/blacklist.vue")
          },
          {
            path: "unlock-codes",
            name: "vo-configurator-unlock-codes",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Unlock Codes" },
            component: () => import("@/modules/configurator/unlock-codes.vue")
          },
          {
            path: "unlock-codes/new",
            name: "vo-configurator-unlock-codes-new",
            meta: { module: "configurator", right: "create", title: titlePrefix + "Configurator Unlock Code New" },
            component: () => import("@/modules/configurator/unlock-codes-new.vue")
          },
          {
            path: "unlock-codes/edit/:id",
            name: "vo-configurator-unlock-codes-edit",
            meta: { module: "configurator", right: "edit", title: titlePrefix + "Configurator Unlock Code Edit" },
            component: () => import("@/modules/configurator/unlock-codes-edit.vue"),
            props: true
          },
          {
            path: "settings",
            name: "vo-configurator-settings",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Settings" },
            component: () => import("@/modules/configurator/settings.vue")
          },
          {
            path: "settings/new",
            name: "vo-configurator-settings-new",
            meta: { module: "configurator", right: "create", title: titlePrefix + "Configurator Settings New" },
            component: () => import("@/modules/configurator/settings-new.vue")
          },
          {
            path: "settings/edit/:id",
            name: "vo-configurator-settings-edit",
            meta: { module: "configurator", right: "edit", title: titlePrefix + "Configurator Settings Edit" },
            component: () => import("@/modules/configurator/settings-edit.vue"),
            props: true
          },
          {
            path: "sponsors",
            name: "vo-configurator-sponsors",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Sponsors" },
            component: () => import("@/modules/configurator/sponsors.vue")
          },
          {
            path: "options",
            name: "vo-configurator-options",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Options" },
            component: () => import("@/modules/configurator/options.vue")
          },
          {
            path: "brands",
            name: "vo-configurator-brands",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Brands" },
            component: () => import("@/modules/configurator/brands.vue")
          },
          {
            path: "groups",
            name: "vo-configurator-groups",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Groups" },
            component: () => import("@/modules/configurator/groups.vue")
          },
          {
            path: "sports",
            name: "vo-configurator-sports",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Sports" },
            component: () => import("@/modules/configurator/sports.vue")
          },
          {
            path: "delivery-countries",
            name: "vo-configurator-delivery-countries",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Delivery Countries" },
            component: () => import("@/modules/configurator/delivery-countries.vue")
          },
          {
            path: "products",
            name: "vo-configurator-products",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Products" },
            component: () => import("@/modules/configurator/products.vue")
          },
          {
            path: "products/new",
            name: "vo-configurator-products-new",
            meta: { module: "configurator", right: "create", title: titlePrefix + "Configurator Products New" },
            component: () => import("@/modules/configurator/products-new.vue")
          },
          {
            path: "products/edit/:id",
            name: "vo-configurator-products-edit",
            meta: { module: "configurator", right: "edit", title: titlePrefix + "Configurator Products Edit" },
            component: () => import("@/modules/configurator/products-edit.vue"),
            props: true
          },
          {
            path: "tracking",
            name: "vo-configurator-tracking",
            meta: { module: "configurator", right: "list", title: titlePrefix + "Configurator Tracking" },
            component: () => import("@/modules/configurator/tracking.vue")
          }
        ]
      },
      {
        path: "/filemanager",
        name: "vo-filemanager",
        children: [
          {
            path: "",
            name: "vo-filemanager-list",
            meta: { module: "filemanager", right: "list", title: titlePrefix + "CMS List" },
            component: () => import("@/modules/filemanager/list.vue")
          },
          {
            path: "new",
            name: "vo-filemanager-new",
            meta: { module: "filemanager", right: "create", title: titlePrefix + "CMS New" },
            component: () => import("@/modules/filemanager/new.vue")
          },
          {
            path: "filemanager/:id",
            props: true,
            name: "vo-filemanager-edit",
            meta: { module: "filemanager", right: "edit", title: titlePrefix + "CMS New" },
            component: () => import("@/modules/filemanager/edit.vue")
          },
          {
            path: "upload",
            name: "vo-filemanager-upload",
            meta: { module: "filemanager", right: "create", title: titlePrefix + "Filemanager Upload" },
            component: () => import("@/modules/filemanager/upload.vue")
          }
        ]
      },
      {
        path: "/cms",
        name: "vo-cms",
        children: [
          {
            path: "",
            props: route => ({ type: "page" }),
            name: "vo-cms-list",
            meta: { module: "cms", right: "list", title: titlePrefix + "CMS List" },
            component: () => import("@/modules/cms/list.vue")
          },
          {
            path: "new",
            props: route => ({ type: "page" }),
            name: "vo-cms-new",
            meta: { module: "cms", right: "create", title: titlePrefix + "CMS New" },
            component: () => import("@/modules/cms/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-cms-edit",
            meta: { module: "cms", right: "edit", title: titlePrefix + "CMS New" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "copy/:id",
            props: route => ({ id: route.params.id, copy: true }),
            name: "vo-cms-copy",
            meta: { module: "cms", right: "edit", title: titlePrefix + "CMS Copy" },
            component: () => import("@/modules/cms/edit.vue")
          },
          {
            path: "blocks",
            name: "vo-cms-blocks",
            meta: { module: "cms", right: "list", title: titlePrefix + "CMS Blocks" },
            component: () => import("@/modules/cms/blocks-list.vue")
          },
          {
            path: "blocks/new",
            name: "vo-cms-blocks-new",
            meta: {
              module: "cms",
              right: "create",
              title: titlePrefix + "CMS Block New"
            },
            component: () => import("@/modules/cms/blocks-new.vue")
          },
          {
            path: "blocks/edit/:id",
            props: true,
            name: "vo-cms-blocks-edit",
            meta: {
              module: "cms",
              right: "edit",
              title: titlePrefix + "CMS Blocks Edit"
            },
            component: () => import("@/modules/cms/blocks-edit.vue")
          },
          {
            path: "blocks/copy/:id",
            props: route => ({ id: route.params.id, copy: true }),
            name: "vo-cms-blocks-copy",
            meta: {
              module: "cms",
              right: "edit",
              title: titlePrefix + "CMS Blocks Copy"
            },
            component: () => import("@/modules/cms/blocks-edit.vue")
          },
          {
            path: "blocks/publish",
            name: "vo-cms-blocks-publish",
            meta: {
              module: "cms",
              right: "edit",
              title: titlePrefix + "CMS Blocks Publish"
            },
            component: () => import("@/modules/cms/blocks-publish.vue")
          },
          {
            path: "blocks/publish-log",
            name: "vo-cms-blocks-publish-log",
            meta: {
              module: "cms",
              right: "list",
              title: titlePrefix + "CMS Publish Log"
            },
            component: () => import("@/modules/cms/publish-log.vue")
          },
          {
            path: "promotions",
            name: "vo-cms-promotions",
            meta: { module: "cms", right: "list", title: titlePrefix + "CMS Promotions" },
            component: () => import("@/modules/cms/promotions-list.vue")
          },
          {
            path: "promotions/new",
            name: "vo-cms-promotions-new",
            meta: { module: "cms", right: "create", title: titlePrefix + "CMS Promotions New" },
            component: () => import("@/modules/cms/promotions-new.vue")
          },
          {
            path: "promotions/edit/:id",
            props: true,
            name: "vo-cms-promotions-edit",
            meta: { module: "cms", right: "edit", title: titlePrefix + "CMS Promotions Edit" },
            component: () => import("@/modules/cms/promotions-edit.vue")
          },
          {
            path: "about",
            name: "vo-cms-about",
            meta: { module: "cms", title: titlePrefix + "About" },
            component: () => import("@/modules/cms/about.vue")
          },
          {
            path: "contact",
            name: "vo-cms-contact",
            meta: { module: "cms", title: titlePrefix + "Contact" },
            component: () => import("@/modules/cms/contact.vue")
          },
          {
            path: "faq",
            name: "vo-cms-faq",
            meta: { module: "cms", title: titlePrefix + "FAQ" },
            component: () => import("@/modules/cms/faq.vue")
          }
        ]
      },
      {
        path: "/navigation",
        name: "vo-navigation",
        children: [
          {
            path: "",
            name: "vo-navigation-list",
            meta: { module: "navigation", right: "list", title: titlePrefix + "Content Navigation List" },
            component: () => import("@/modules/navigation/list.vue")
          },
          {
            path: "new",
            name: "vo-navigation-new",
            meta: { module: "navigation", right: "create", title: titlePrefix + "Content Navigation New" },
            component: () => import("@/modules/navigation/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-navigation-edit",
            meta: { module: "navigation", right: "edit", title: titlePrefix + "Content Navigation New" },
            component: () => import("@/modules/navigation/edit.vue")
          },
          {
            path: "groups",
            name: "vo-navigation-groups",
            meta: { module: "navigation", right: "list", title: titlePrefix + "Content Navigation Groups" },
            component: () => import("@/modules/navigation/groups-list.vue")
          },
          {
            path: "groups/new",
            name: "vo-navigation-groups-new",
            meta: {
              module: "navigation",
              right: "create",
              title: titlePrefix + "Content Navigation Group New"
            },
            component: () => import("@/modules/navigation/groups-new.vue")
          },
          {
            path: "groups/edit/:id",
            props: true,
            name: "vo-navigation-groups-edit",
            meta: {
              module: "navigation",
              right: "edit",
              title: titlePrefix + "Content Navigation Group Edit"
            },
            component: () => import("@/modules/navigation/groups-edit.vue")
          }
        ]
      },
      {
        path: "/users",
        name: "vo-users",
        children: [
          {
            path: "",
            name: "vo-users-list",
            meta: { module: "users", right: "list", title: titlePrefix + "Users List" },
            component: () => import("@/modules/users/list.vue")
          },
          {
            path: "new",
            name: "vo-users-new",
            meta: { module: "users", right: "create", title: titlePrefix + "Users New" },
            component: () => import("@/modules/users/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-users-edit",
            meta: { module: "users", right: "edit", title: titlePrefix + "Users New" },
            component: () => import("@/modules/users/edit.vue")
          }
        ]
      },
      {
        path: "/licenses",
        name: "vo-licenses",
        children: [
          {
            path: "",
            name: "vo-licenses-list",
            meta: { module: "licenses", right: "list", title: titlePrefix + "Licenses List" },
            component: () => import("@/modules/licenses/list.vue")
          },
          {
            path: "new",
            name: "vo-licenses-new",
            meta: { module: "licenses", right: "create", title: titlePrefix + "Storage New" },
            component: () => import("@/modules/licenses/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-licenses-edit",
            meta: { module: "licenses", right: "edit", title: titlePrefix + "Storage Edit" },
            component: () => import("@/modules/licenses/edit.vue")
          },
          {
            path: "system-modules",
            name: "vo-licenses-system-modules",
            meta: { module: "licenses", right: "list", title: titlePrefix + "System Modules" },
            component: () => import("@/modules/licenses/system-modules.vue")
          },
          {
            path: "dashboard-widgets",
            name: "vo-licenses-dashboard-widgets",
            meta: { module: "licenses", right: "list", title: titlePrefix + "Dashboard Widgets" },
            component: () => import("@/modules/licenses/dashboard-widgets.vue")
          },
          {
            path: "notification-events",
            name: "vo-licenses-notification-events",
            meta: { module: "licenses", right: "list", title: titlePrefix + "Notification Events" },
            component: () => import("@/modules/licenses/notification-events.vue")
          },
          {
            path: "system-blocks",
            name: "vo-licenses-system-blocks",
            meta: { module: "licenses", right: "list", title: titlePrefix + "System Blocks" },
            component: () => import("@/modules/licenses/system-blocks.vue")
          },
          {
            path: "resolve-urls",
            name: "vo-licenses-resolve-urls",
            meta: { module: "licenses", right: "list", title: titlePrefix + "Resolve URLs" },
            component: () => import("@/modules/licenses/resolve-urls.vue")
          },
          {
            path: "dev-sync",
            name: "vo-licenses-dev-sync",
            meta: { module: "licenses", right: "edit", title: titlePrefix + "DEV DB Sync" },
            component: () => import("@/modules/licenses/dev-sync.vue")
          }
        ]
      },
      {
        path: "/customers",
        name: "vo-customers",
        children: [
          {
            path: "",
            name: "vo-customers-list",
            meta: { module: "customers", right: "list", title: titlePrefix + "Customers List" },
            component: () => import("@/modules/customers/list.vue")
          },
          {
            path: "new",
            name: "vo-customers-new",
            meta: { module: "customers", right: "create", title: titlePrefix + "Customers New" },
            component: () => import("@/modules/customers/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-customers-edit",
            meta: { module: "customers", right: "edit", title: titlePrefix + "Customers Edit" },
            component: () => import("@/modules/customers/edit.vue")
          }
        ]
      },
      {
        path: "/warehouses",
        name: "vo-warehouses",
        children: [
          {
            path: "",
            name: "vo-warehouses-list",
            meta: { module: "warehouses", right: "list", title: titlePrefix + "Warehouses List" },
            component: () => import("@/modules/warehouses/list.vue")
          },
          {
            path: "new",
            name: "vo-warehouses-new",
            meta: { module: "warehouses", right: "create", title: titlePrefix + "Warehouses New" },
            component: () => import("@/modules/warehouses/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-warehouses-edit",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses New" },
            component: () => import("@/modules/warehouses/edit.vue")
          },
          {
            path: ":warehouseId/locations",
            props: true,
            name: "vo-warehouses-locations",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Locations" },
            component: () => import("@/modules/warehouses/locations.vue")
          },
          {
            path: ":warehouseId/location/edit/:locationId",
            props: true,
            name: "vo-warehouses-location-edit",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Location Edit" },
            component: () => import("@/modules/warehouses/location-edit.vue")
          },
          {
            path: ":warehouseId/location/new",
            props: true,
            name: "vo-warehouses-location-new",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Location New" },
            component: () => import("@/modules/warehouses/location-new.vue")
          },
          {
            path: ":warehouseId/packagings",
            props: true,
            name: "vo-warehouses-packagings",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Packaging" },
            component: () => import("@/modules/warehouses/packagings.vue")
          },
          {
            path: ":warehouseId/packaging/edit/:packagingId",
            props: true,
            name: "vo-warehouses-packaging-edit",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Packaging Edit" },
            component: () => import("@/modules/warehouses/packaging-edit.vue")
          },
          {
            path: ":warehouseId/packaging/new",
            props: true,
            name: "vo-warehouses-packaging-new",
            meta: { module: "warehouses", right: "edit", title: titlePrefix + "Warehouses Packaging New" },
            component: () => import("@/modules/warehouses/packaging-new.vue")
          },
          {
            path: "reports",
            name: "vo-warehouses-reports",
            meta: { module: "warehouses", right: "report", title: titlePrefix + "Warehouses Reports" },
            component: () => import("@/modules/warehouses/reports.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-warehouses-print",
            meta: { module: "warehouses", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/warehouses/print.vue")
          }
        ]
      },
      {
        path: "/connectivity",
        name: "vo-connectivity",
        children: [
          {
            path: "",
            name: "vo-connectivity-list",
            meta: { module: "connectivity", right: "list", title: titlePrefix + "Connectivity List" },
            component: () => import("@/modules/connectivity/list.vue")
          },
          {
            path: "new",
            name: "vo-connectivity-new",
            meta: { module: "connectivity", right: "create", title: titlePrefix + "Connectivity New" },
            component: () => import("@/modules/connectivity/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-connectivity-edit",
            meta: { module: "connectivity", right: "edit", title: titlePrefix + "Connectivity Edit" },
            component: () => import("@/modules/connectivity/edit.vue")
          }
        ]
      },
      {
        path: "/items",
        name: "vo-items",
        children: [
          {
            path: "",
            name: "vo-items-list",
            meta: { module: "items", right: "list", title: titlePrefix + "Items List" },
            component: () => import("@/modules/items/list.vue")
          },
          {
            path: "new",
            name: "vo-items-new",
            meta: { module: "items", right: "create", title: titlePrefix + "Items New" },
            component: () => import("@/modules/items/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-items-edit",
            meta: { module: "items", right: "edit", title: titlePrefix + "Items Edit" },
            component: () => import("@/modules/items/edit.vue")
          },
          {
            path: "copy/:id",
            props: route => ({ id: route.params.id, copy: true }),
            name: "vo-items-copy",
            meta: { module: "items", right: "edit", title: titlePrefix + "Item Copy" },
            component: () => import("@/modules/items/edit.vue")
          },
          {
            path: "variants/:id",
            props: true,
            name: "vo-items-variants",
            meta: { module: "items", right: "edit", title: titlePrefix + "Items Variants" },
            component: () => import("@/modules/items/variants.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-items-print",
            meta: { module: "items", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/items/print.vue")
          }
        ]
      },
      {
        path: "/receiving",
        name: "vo-receiving",
        children: [
          {
            path: "",
            name: "vo-receiving-list",
            meta: { module: "receiving", right: "list", title: titlePrefix + "Receiving List" },
            component: () => import("@/modules/receiving/list.vue")
          },
          {
            path: "new/:type",
            props: true,
            name: "vo-receiving-new",
            meta: { module: "receiving", right: "create", title: titlePrefix + "Receiving New" },
            component: () => import("@/modules/receiving/new.vue")
          },
          {
            path: "convert/:id/:convert",
            props: true,
            name: "vo-receiving-convert",
            meta: { module: "receiving", right: "edit", title: titlePrefix + "Receiving Convert" },
            component: () => import("@/modules/receiving/convert.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-receiving-edit",
            meta: { module: "receiving", right: "edit", title: titlePrefix + "Receiving Edit" },
            component: () => import("@/modules/receiving/edit.vue")
          },
          {
            path: "confirm/:id",
            props: true,
            name: "vo-receiving-confirm",
            meta: { module: "receiving", right: "edit", title: titlePrefix + "Receiving Confirm" },
            component: () => import("@/modules/receiving/confirm.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-receiving-print",
            meta: { module: "receiving", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/receiving/print.vue")
          }
        ]
      },
      {
        path: "/orders",
        name: "vo-orders",
        children: [
          {
            path: "",
            name: "vo-orders-list",
            meta: { module: "orders", right: "list", title: titlePrefix + "Orders List" },
            component: () => import("@/modules/orders/list.vue")
          },
          {
            path: "new",
            name: "vo-orders-new",
            meta: { module: "orders", right: "create", title: titlePrefix + "Orders New" },
            component: () => import("@/modules/orders/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-orders-edit",
            meta: { module: "orders", right: "edit", title: titlePrefix + "Orders Edit" },
            component: () => import("@/modules/orders/edit.vue")
          },
          {
            path: "confirm-conflict/:id",
            props: true,
            name: "vo-orders-confirm-conflict",
            meta: { module: "orders", right: "edit", title: titlePrefix + "Orders Confirm Conflict" },
            component: () => import("@/modules/orders/confirm-conflict.vue")
          },
          {
            path: "confirm-picklist/:id",
            props: true,
            name: "vo-orders-confirm-picklist",
            meta: { module: "orders", right: "edit", title: titlePrefix + "Orders Confirm Picklist" },
            component: () => import("@/modules/orders/confirm-picklist.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-orders-print",
            meta: { module: "orders", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/orders/print.vue")
          }
        ]
      },
      {
        path: "/shipment",
        name: "vo-shipment",
        children: [
          {
            path: "",
            name: "vo-shipment-list",
            meta: { module: "shipment", right: "list", title: titlePrefix + "Shipment List" },
            component: () => import("@/modules/shipment/list.vue")
          },
          {
            path: "confirm/:id",
            props: true,
            name: "vo-shipment-confirm",
            meta: { module: "shipment", right: "edit", title: titlePrefix + "Shipment Confirm" },
            component: () => import("@/modules/shipment/confirm.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-shipment-print",
            meta: { module: "shipment", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/shipment/print.vue")
          }
        ]
      },
      {
        path: "/inventory",
        name: "vo-inventory",
        children: [
          {
            path: "",
            name: "vo-inventory-list",
            meta: { module: "inventory", right: "list", title: titlePrefix + " Inventory List" },
            component: () => import("@/modules/inventory/list.vue")
          },
          {
            path: "new",
            name: "vo-inventory-new",
            meta: { module: "inventory", right: "create", title: titlePrefix + "Inventory New" },
            component: () => import("@/modules/inventory/new.vue")
          },
          {
            path: "edit/:id",
            props: true,
            name: "vo-inventory-edit",
            meta: { module: "inventory", right: "edit", title: titlePrefix + " Inventory Edit" },
            component: () => import("@/modules/inventory/edit.vue")
          },
          {
            path: "print/:type/:id",
            props: true,
            name: "vo-inventory-print",
            meta: { module: "inventory", right: "list", title: titlePrefix + "Print" },
            component: () => import("@/modules/inventory/print.vue")
          }
        ]
      },
      {
        path: "/account",
        name: "vo-account",
        children: [
          {
            path: "",
            name: "vo-account-list",
            meta: { module: "account", right: "list", title: titlePrefix + " Account" },
            component: () => import("@/modules/account/settings.vue")
          },
          {
            path: "settings",
            name: "vo-account-settings",
            meta: { module: "account", title: titlePrefix + "Account :: Settings" },
            component: () => import("@/modules/account/settings.vue")
          },
          {
            path: "widgets",
            name: "vo-account-widgets",
            meta: { module: "account", title: titlePrefix + "Account :: Widgets" },
            component: () => import("@/modules/account/widgets.vue")
          }
        ]
      },
      {
        path: "/checkout",
        name: "vo-checkout",
        children: [
          {
            path: "",
            name: "vo-checkout-stats",
            meta: { module: "checkout", right: "report", title: titlePrefix + " Checkout Stats" },
            component: () => import("@/modules/checkout/stats.vue")
          },
          {
            path: "reminder",
            name: "vo-checkout-reminder",
            meta: { module: "checkout", right: "list", title: titlePrefix + " Checkout Reminder" },
            component: () => import("@/modules/checkout/reminder.vue")
          }
        ]
      }
    ]
  },
  {
    path: "/login",
    name: "vo-login",
    meta: { module: "system", title: titlePrefix + "Login" },
    component: () => import("@/modules/login/index.vue"),
    children: []
  },
  {
    path: "/login/token/:token/",
    name: "vo-login-token",
    props: true,
    meta: { module: "system", title: titlePrefix + "Login" },
    component: () => import("@/modules/login/token-login.vue")
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "vo-error",
    meta: { module: "system", title: titlePrefix + "404" },
    component: () => import("@/modules/error/index.vue")
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404"
  }
];

const router = createRouter({
  //history: createWebHashHistory(),
  history: createWebHistory(),
  routes
});

router.beforeEach((to, from, next) => {

  if (to.name === "vo-login" || to.name === "vo-login-token") {

    // reset config to initial state
    store.commit(Mutations.RESET_LAYOUT_CONFIG);

    return next();
  }

  // check for edit mode //
  const isEditing = store.getters.getIsEditing;
  if (isEditing) {
    if (!window.confirm("Leave without saving?")) {
      return;
    }
  }

  // manual reloading //
  if (!store.getters.isUserFetched) {

    // dispatch user data also on reload page needed //
    store.dispatch(Actions.FETCH_USER).then(() => next()).catch(() => {

      store.dispatch(Actions.LOGOUT).then();
      router.push({ name: "vo-login" }).then();

    });

  } else {

    //console.log('router beforeEach');

    if (false) {
      store.dispatch(Actions.IS_LOADING, true);
    }

    next();

  }

});

router.beforeResolve((to, from, next) => {

  // check for route authentication //
  store.dispatch(Actions.CHECK_AUTH, to).then(() => {

    // @ts-ignore
    document.title = to.meta.title || "";

    // Scroll page to top on every route change
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);

    next();

  }).catch(() => {

    const headline = "You do not have the necessary access rights!";
    const button = "Oh, ok!";
    utilsService.showErrorOverlay(headline, button);

    return false;

  });

});

router.afterEach((to, from) => {

  nextTick(() => {

    if (to.name !== "vo-login" && to.name !== "vo-login-token") {

      // set current last call from route to history stack //
      store.dispatch(Actions.ADD_ROUTE_HISTORY, { ...to });

    }

  }).then(() => {

    if (false) {
      setTimeout(() => {
        store.dispatch(Actions.IS_LOADING, false);
      }, 500);
    }

  });

});

export default router;
